/**
 * A code check's options, as a form built from its option specs. Values are
 * kept as the form shows them (a list as "a, b") and converted when sent.
 */

import { Select } from "@/components/ui/select";
import type { CheckOption } from "./types";

export type FormValue = string | boolean;

const choiceOf = (c: string | { value: string; label: string }) =>
  typeof c === "string" ? { value: c, label: c } : c;

/** A stored option value as the form shows it. */
export function toForm(spec: CheckOption, value: unknown): FormValue {
  if (spec.type === "bool") return value === true;
  if (value === undefined || value === null) return "";
  if (Array.isArray(value)) return value.join(", ");
  return String(value);
}

/** What the form holds, as the server wants it; undefined leaves it out. */
export function fromForm(spec: CheckOption, value: FormValue): unknown {
  if (spec.type === "bool") return value === true;
  const text = String(value).trim();
  if (spec.type === "list")
    return text
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  if (!text) return undefined;
  if (spec.type === "number") return Number(text);
  return text;
}

/** Every option of a check, its default filled in where the form has none. */
export function formOptions(specs: CheckOption[], form: Record<string, FormValue>) {
  return Object.fromEntries(specs.map((s) => [s.key, form[s.key] ?? toForm(s, s.default)]));
}

export function optionsPayload(specs: CheckOption[], form: Record<string, FormValue>) {
  const filled = formOptions(specs, form);
  return Object.fromEntries(
    specs.map((s) => [s.key, fromForm(s, filled[s.key]!)] as const).filter(([, v]) => v !== undefined),
  );
}

export function CheckOptions({
  specs,
  value,
  onChange,
}: {
  specs: CheckOption[];
  value: Record<string, FormValue>;
  onChange: (key: string, value: FormValue) => void;
}) {
  if (specs.length === 0) return null;
  const shown = formOptions(specs, value);
  return (
    <fieldset className="flex flex-col gap-2.5">
      <legend className="mb-1.5 text-[13px] font-semibold">Options</legend>
      {specs.map((s) => {
        const v = shown[s.key];
        if (s.type === "bool")
          return (
            <label key={s.key} className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                className="size-4 accent-ink"
                checked={v === true}
                onChange={(e) => onChange(s.key, e.target.checked)}
              />
              {s.label}
            </label>
          );
        const id = `check-opt-${s.key}`;
        return (
          <div key={s.key} className="grid grid-cols-[150px_minmax(0,1fr)] items-center gap-2 text-[13px]">
            <label htmlFor={id}>{s.label}</label>
            {s.type === "select" ? (
              <Select
                id={id}
                value={String(v)}
                onChange={(c) => onChange(s.key, c)}
                options={(s.choices ?? []).map(choiceOf)}
              />
            ) : (
              <input
                id={id}
                className="inp"
                type={s.type === "number" ? "number" : "text"}
                step="any"
                placeholder={s.type === "list" ? "Separate with commas" : undefined}
                value={String(v)}
                onChange={(e) => onChange(s.key, e.target.value)}
              />
            )}
          </div>
        );
      })}
    </fieldset>
  );
}
